<template>
  <v-dialog max-width="600px" v-model="showDialog" @update:model-value="handleDialogClose"
            @keydown.esc="handleEscape">
    <v-card>
      <v-card-title>
        <h4>Edit Service</h4>
      </v-card-title>
      <v-card-text>
        <v-text-field v-model="form.name" label="Service name" :rules="inputRulesName"></v-text-field>
        <v-text-field v-model="form.public_name" label="Service public name" :rules="inputRulesName"></v-text-field>
        <v-text-field v-model="form.description" label="Service description"></v-text-field>

        <v-select
          v-model="form.mvno_id"
          :items="mvnos"
          item-value="id"
          item-title="name"
          label="MVNO"
          outlined
          dense
          clearable
        ></v-select>

        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.service_type_id"
              :items="serviceTypes"
              item-value="id"
              item-title="name"
              label="Service Type"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.applied_to_client_type_id"
              :items="clientTypes"
              item-value="id"
              item-title="name"
              label="Applied to client type"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>

        <v-row class="align-center mb-3">
          <v-col cols="4">
            <v-switch
              v-model="form.available_to_subclients"
              label="Available to subclients"
              :color="form.available_to_subclients ? 'success' : 'error'"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="form.self_applied"
              label="Self Applied"
              :color="form.self_applied ? 'success' : 'error'"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="form.allow_multiple"
              label="Allow Multiple"
              :color="form.allow_multiple ? 'success' : 'error'"
              :disabled="isSubscriptionType"
              :hint="isSubscriptionType ? 'Disabled for subscription services' : ''"
              :persistent-hint="isSubscriptionType"
              hide-details="auto"
            ></v-switch>
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="form.needs_delivery"
              label="Needs Delivery"
              :color="form.needs_delivery ? 'success' : 'error'"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="form.show_in_search"
              label="Show in Search"
              :color="form.show_in_search ? 'primary' : 'error'"
              hide-details
            ></v-switch>
          </v-col>
        </v-row>

        <v-row class="mb-3">
          <v-col cols="6">
            <v-text-field
              v-model="form.label_on_search_button"
              label="Label on Search Button"
              hint="Leave empty to use service name"
              persistent-hint
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Payment Method Types -->
        <v-row class="mb-3">
          <v-col cols="12">
            <div class="text-subtitle-2 mb-2">Allowed Payment Methods</div>
            <v-chip-group
              v-model="form.payment_method_type_ids"
              multiple
              column
              selected-class="text-primary"
            >
              <v-chip
                v-for="pmt in paymentMethodTypes"
                :key="pmt.id"
                :value="pmt.id"
                filter
                variant="outlined"
              >
                {{ pmt.name }}
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.billing_type_id"
              :items="billingTypes"
              item-value="id"
              item-title="name"
              label="Billing Type"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.billing_period_id"
              :items="billingPeriods"
              item-value="id"
              item-title="name"
              label="Billing Period"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <v-text-field v-model="form.service_price" label="Price" :rules="inputRulesPrice"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="form.currency_id"
              :items="currencies"
              item-value="id"
              item-title="name"
              label="Currency"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="form.validity" label="Validity (days)"></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="space-around" class="mt-3 mb-3">
          <v-btn @click="closeDialog" large color="error">Cancel</v-btn>
          <v-btn large color="primary" @click="updateServiceLocal" :loading="loading">Save Changes</v-btn>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { log } from "@/composables/useLog";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { getServicesGeneralSettings } from "@/composables/useGetGeneralSettings";
import { updateService } from "@/composables/useUpdateService";

export default {
  props: {
    service: {
      type: Object,
      required: true
    }
  },
  emits: ["serviceUpdatedEvent", "closeEditServiceEvent"],
  data() {
    return {
      showDialog: true,
      loading: false,
      token: '',
      form: {
        id: null,
        name: '',
        public_name: '',
        description: '',
        service_type_id: null,
        applied_to_client_type_id: null,
        available_to_subclients: false,
        self_applied: false,
        allow_multiple: false,
        needs_delivery: false,
        show_in_search: false,
        label_on_search_button: '',
        billing_type_id: null,
        billing_period_id: null,
        currency_id: null,
        mvno_id: null,
        service_price: null,
        validity: null,
        payment_method_type_ids: []
      },
      clientTypes: [],
      serviceTypes: [],
      billingTypes: [],
      billingPeriods: [],
      currencies: [],
      mvnos: [],
      paymentMethodTypes: [],
      inputRulesName: [
        v => !!v || 'This field is required',
        v => (v && v.length >= 3) || 'Minimum length is 3 characters'
      ],
      inputRulesPrice: [
        (value) => !!value || "This field is required.",
        (value) =>
          /^\d{1,6}(\.\d{1,2})?$/.test(value) ||
          "Must be a number with up to 6 digits and 2 decimals.",
      ],
    };
  },
  computed: {
    isSubscriptionType() {
      if (!this.form.service_type_id || !this.serviceTypes.length) return false
      const st = this.serviceTypes.find(t => t.id === this.form.service_type_id)
      return st?.name === 'imsi_subscription'
    }
  },
  watch: {
    'form.service_type_id'() {
      if (this.isSubscriptionType) {
        this.form.allow_multiple = false
      }
    }
  },
  methods: {
    handleDialogClose(value) {
      if (!value) {
        this.$emit('closeEditServiceEvent');
      }
    },
    handleEscape() {
      this.$emit('closeEditServiceEvent');
    },
    closeDialog() {
      this.showDialog = false;
      this.$emit('closeEditServiceEvent');
    },
    async updateServiceLocal() {
      log("EditService - updateServiceLocal() called");
      log("form data being sent:");
      log(this.form);
      log("form.name:", this.form.name);
      log("form.public_name:", this.form.public_name);
      log("form.description:", this.form.description);

      if (!this.form.name || this.form.name === '') {
        alert("Name cannot be empty");
        return;
      }
      if (!this.form.service_price) {
        alert("Price cannot be empty");
        return;
      }

      this.loading = true;
      try {
        const response = await updateService(this.token, this.form);
        log("EditService - API response:");
        log(response);
        if (response.error) {
          alert(response.message || "Error updating service");
        } else {
          this.$emit("serviceUpdatedEvent");
          this.showDialog = false;
        }
      } catch (err) {
        console.error("Error updating service:", err);
        alert("Unexpected error occurred");
      }
      this.loading = false;
    },
    populateForm() {
      log("EditService - populateForm() called with service:");
      log(this.service);

      this.form.id = this.service.id;
      // Handle name - use service.name if it exists and is not empty, otherwise use public_name as fallback
      this.form.name = (this.service.name && this.service.name.trim() !== '')
        ? this.service.name
        : (this.service.public_name || '');
      this.form.public_name = this.service.public_name || '';
      this.form.description = this.service.description || '';
      this.form.service_type_id = this.service.service_type_id;
      this.form.applied_to_client_type_id = this.service.applied_to_client_type_id;
      this.form.available_to_subclients = this.service.available_to_subclients || false;
      this.form.self_applied = this.service.self_applied || false;
      this.form.allow_multiple = this.service.allow_multiple || false;
      this.form.needs_delivery = this.service.needs_delivery || false;
      this.form.show_in_search = this.service.show_in_search || false;
      this.form.label_on_search_button = this.service.label_on_search_button || '';
      this.form.billing_type_id = this.service.billing_type_id;
      this.form.billing_period_id = this.service.billing_period_id;
      this.form.currency_id = this.service.currency_id;
      this.form.mvno_id = this.service.mvno_id;
      this.form.service_price = this.service.service_price;
      this.form.validity = this.service.validity;
      this.form.payment_method_type_ids = this.service.payment_method_type_ids || [];

      log("EditService - form after populate:");
      log(this.form);
    }
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      this.token = token.value;
      getServicesGeneralSettings().then((response) => {
        log("EditService - loaded settings");
        this.clientTypes = response.client_types;
        this.serviceTypes = response.service_types;
        this.currencies = response.currencies;
        this.billingTypes = response.billing_types;
        this.billingPeriods = response.billing_periods;
        this.paymentMethodTypes = response.payment_method_types || [];
        this.mvnos = response.mvnos || [];
        this.populateForm();
      });
    });
  }
};
</script>
